import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Initializing...');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        setStatus('Reading authorization data...');
        
        // Get API URL - use origin as base
        const API = `${window.location.origin}/api`;
        console.log('Using API:', API);
        
        // Check for Google OAuth code in query params
        const queryParams = new URLSearchParams(location.search);
        const googleCode = queryParams.get('code');
        const state = queryParams.get('state') || '/dashboard';
        
        console.log('Google code present:', !!googleCode);
        console.log('State:', state);
        
        if (googleCode) {
          setStatus('Exchanging code with server...');
          
          const currentRedirectUri = `${window.location.origin}/auth/callback`;
          console.log('Redirect URI:', currentRedirectUri);
          
          const response = await axios.get(`${API}/auth/google/exchange`, {
            params: {
              code: googleCode,
              state: state,
              redirect_uri: currentRedirectUri
            }
          });
          
          console.log('Exchange response:', response.data);
          setStatus('Creating session...');
          
          const { session_token, user_id, email, name, picture, workspace_id } = response.data;
          
          localStorage.setItem('session_token', session_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;
          
          const cookieOptions = `path=/; max-age=${7 * 24 * 60 * 60}; SameSite=None; Secure`;
          document.cookie = `session_token=${session_token}; ${cookieOptions}`;
          
          const userData = { user_id, email, name, picture, workspace_id };
          setAuthUser(userData, session_token);
          
          toast.success(`Welcome, ${name}!`);
          navigate(state, { replace: true, state: { user: userData, token: session_token } });
          return;
        }
        
        // Check hash for session_token (from backend callback)
        const hash = location.hash;
        const hashParams = new URLSearchParams(hash.substring(1));
        const directSessionToken = hashParams.get('session_token');
        
        if (directSessionToken) {
          setStatus('Processing session token...');
          
          const session_token = directSessionToken;
          const user_id = hashParams.get('user_id');
          const email = decodeURIComponent(hashParams.get('email') || '');
          const name = decodeURIComponent(hashParams.get('name') || '');
          const picture = decodeURIComponent(hashParams.get('picture') || '');
          const workspace_id = hashParams.get('workspace_id') || null;

          localStorage.setItem('session_token', session_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;
          
          const cookieOptions = `path=/; max-age=${7 * 24 * 60 * 60}; SameSite=None; Secure`;
          document.cookie = `session_token=${session_token}; ${cookieOptions}`;

          const userData = { user_id, email, name, picture, workspace_id };
          setAuthUser(userData, session_token);

          toast.success(`Welcome, ${name}!`);
          navigate('/dashboard', { replace: true, state: { user: userData, token: session_token } });
          return;
        }
        
        // Legacy session_id flow
        const sessionId = hashParams.get('session_id');
        if (sessionId) {
          setStatus('Exchanging session ID...');
          
          const response = await axios.get(`${API}/auth/session-data`, {
            headers: { 'X-Session-ID': sessionId }
          });

          const { session_token, user_id, email, name, picture, workspace_id } = response.data;

          localStorage.setItem('session_token', session_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;
          
          const cookieOptions = `path=/; max-age=${7 * 24 * 60 * 60}; SameSite=None; Secure`;
          document.cookie = `session_token=${session_token}; ${cookieOptions}`;

          const userData = { user_id, email, name, picture, workspace_id };
          setAuthUser(userData, session_token);

          toast.success(`Welcome, ${name}!`);
          navigate('/dashboard', { replace: true, state: { user: userData, token: session_token } });
          return;
        }
        
        throw new Error('No authentication data found in URL');

      } catch (err) {
        console.error('Auth error:', err);
        const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
        setError(errorMsg);
        toast.error(`Authentication failed: ${errorMsg}`);
        setTimeout(() => navigate('/'), 3000);
      }
    };

    processAuth();
  }, [location, navigate, setAuthUser]);

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <div className="text-center p-8">
        {error ? (
          <div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Authentication Failed</h2>
            <p className="text-zinc-600 mb-2">{error}</p>
            <p className="text-zinc-400 text-sm">Redirecting to home in 3 seconds...</p>
          </div>
        ) : (
          <div>
            <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold mb-2" style={{fontFamily: 'Outfit'}}>Signing you in...</h2>
            <p className="text-zinc-600">{status}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
